import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, Listing, Kind } from "@/lib/api";
import { PageHeader, Spinner, EmptyState } from "@/components/site/Bits";
import { ListingCard } from "@/components/site/ListingCard";
import { Waitlist2027 } from "@/components/site/Waitlist2027";

export const STAY_CATEGORIES = ["Studio", "1 Bedroom", "2 Bedroom", "3 Bedroom", "4+ Bedroom", "House", "Other"];
export const VEHICLE_CLASSES = ["Economy", "Compact", "Sedan", "SUV", "Luxury", "Family", "Van"];

const Chips = ({ param, values, all, params, setParams }: { param: string; values: string[]; all: string; params: URLSearchParams; setParams: (p: URLSearchParams) => void }) => {
  const cur = params.get(param) || "";
  return (
    <div className="flex flex-wrap gap-2" data-testid={`${param}-filter`}>
      {["", ...values].map((c) => (
        <button key={c || "all"} onClick={() => { const p = new URLSearchParams(params); c ? p.set(param, c) : p.delete(param); setParams(p); }}
          className={`rounded-full border px-4 py-2 text-sm font-medium transition-colors ${cur === c ? "border-navy bg-navy text-white" : "border-navy/15 hover:bg-navy/5"}`} data-testid={`${param}-filter-${(c || "all").toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
          {c || all}
        </button>
      ))}
    </div>
  );
};

const ListingsPage = ({ kind, eyebrow, title, intro, cities, cats, after }: { kind: Kind; eyebrow: string; title: string; intro: string; cities?: string[]; cats: string[]; after?: React.ReactNode }) => {
  const [params, setParams] = useSearchParams();
  const city = params.get("city") || "";
  const category = params.get("category") || "";
  const guests = Number(params.get("guests") || 0);
  const [items, setItems] = useState<Listing[] | null>(null);
  useEffect(() => {
    setItems(null);
    const q: any = {};
    if (city) q.city = city;
    if (category) q.category = category;
    api.get(`/listings/${kind}`, { params: q }).then((r) => setItems(r.data)).catch(() => setItems([]));
  }, [kind, city, category]);
  const shown = (items || []).filter((it) => !guests || !it.capacity || it.capacity >= guests);
  const qs = ["start", "end", "guests"].filter((k) => params.get(k)).map((k) => `${k}=${params.get(k)}`).join("&");
  return (
    <div>
      <PageHeader eyebrow={eyebrow} title={title} intro={intro} testid={`${kind}-page-title`} />
      <div className="container-x py-12">
        <div className="mb-8 space-y-3">
          {cities && <Chips param="city" values={cities} all="All cities" params={params} setParams={setParams} />}
          <Chips param="category" values={cats} all={kind === "stay" ? "All property types" : "All classes"} params={params} setParams={setParams} />
        </div>
        {!items ? <Spinner /> : shown.length === 0 ? (
          <EmptyState testid={`${kind}-empty`} title={items.length || city || category ? "No matches for your filters" : "Listings are being prepared"}
            body={items.length || city || category ? "Try a different city, category or group size." : "We only publish listings that are verified and ready to book. Please check back soon, or contact our team and we will help you directly."}
            cta="Contact support" to="/support" />
        ) : (
          <div className="grid gap-x-6 gap-y-12 sm:grid-cols-2 lg:grid-cols-3" data-testid={`${kind}-grid`}>
            {shown.map((it, i) => <div key={it.id} onClickCapture={() => qs && sessionStorage.setItem("tl_search", qs)}><ListingCard item={it} kind={kind} i={i} /></div>)}
          </div>
        )}
      </div>
      {after}
    </div>
  );
};

export const Stays = () => (
  <ListingsPage kind="stay" eyebrow="Accommodation · Accra, Ghana" title="Stays in Accra" cats={STAY_CATEGORIES}
    intro="Studios to family homes in Accra, each reviewed by our team. Submit a request and we confirm availability personally." after={<Waitlist2027 />} />
);

export const Vehicles = () => (
  <ListingsPage kind="vehicle" eyebrow="Vehicle rentals · El Paso · Houston · Accra" title="Our Vehicle Collection" cities={["El Paso", "Houston", "Accra"]} cats={VEHICLE_CLASSES}
    intro="From everyday sedans to luxury SUVs. Photos show each model; the exact vehicle, availability and rate are confirmed with you before anything is booked." />
);
